/**
 * Senha e sessões — senha, autenticador e sessões, sem a pessoa ver o
 * Keycloak (decisão da dona, 2026-10-14: só a TI vê o Keycloak; o resto
 * é feito pelo site e enviado pra ele).
 *
 * Três peças, cada uma do jeito que o Keycloak permite fazer sem expor
 * o painel dele:
 *
 * - **Conta Google**: senha e verificação em duas etapas são as do
 *   Google. Não há o que fazer aqui, e a tela diz onde mexer.
 * - **Exceção por senha**: "Trocar senha", "Configurar autenticador" e
 *   "Cadastrar chave de acesso" (passkey, 2026-10-21: entra com digital,
 *   rosto ou PIN, sem senha e sem código) mandam ao realm com
 *   `kc_action` (Application Initiated Action). A
 *   tela que aparece é a de login, que já tem a cara do ReputAItion, e a
 *   pessoa volta pra cá no fim. Um formulário de senha NOSSO obrigaria a
 *   API a receber a senha em claro e a conferir a atual por um fluxo que
 *   o próprio Keycloak desaconselha.
 * - **Sessões**: as do ReputAItion, lidas e encerradas pela API
 *   (`/me/sessions`), sempre da própria pessoa.
 *
 * É uma seção de Configurações, no mesmo painel das outras (ver
 * `ProfilePage`). Os cartões são os `card account-card` das páginas de
 * conta; dentro deles, cada item (senha, autenticador, sessão) é uma linha
 * `security-row` com a ação sempre na mesma coluna, à direita (redesenho de
 * 2026-10-15 — o "Sair" ficava desalinhado).
 */

import { Fingerprint, KeyRound, LogOut, Monitor, ShieldCheck, Smartphone, Trash2 } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { apiClient, type MyPasskey, type MySession } from "@/shared/api/api-client";
import { errorMessage } from "@/shared/api/http";
import { beginLogin, oidcEnabled, type AccountAction } from "@/shared/auth/oidc";
import { ApiState } from "@/shared/components/ApiState";
import { parseServerTime } from "@/features/uploads/upload-metrics";
import { formatDateTime } from "@/shared/utils/format";
import { useAppShell } from "@/shell/app-shell-context";

function serverDate(value: string): Date | null {
  const millis = parseServerTime(value);
  return millis == null ? null : new Date(millis);
}

export function SecurityPage() {
  const { token, profile, logout } = useAppShell();
  // Com o login por pessoa ligado, quem é `google` entra pelo Google e não
  // tem senha nossa. Com o modo desligado todo mundo entra com senha.
  const signsInWithGoogle = Boolean(profile.login_mode) && profile.login_method === "google";

  const [sessions, setSessions] = useState<MySession[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  // Separado do `actionError`: a falha ao encerrar aparece junto da lista
  // de sessões, não no cartão de senha.
  const [sessionError, setSessionError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Descarte por CARGA: a resposta de uma leitura antiga não sobrescreve
  // a de uma mais nova (encerrar e recarregar em seguida).
  const loadSeq = useRef(0);

  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    setLoadError(null);
    try {
      const rows = await apiClient.listMySessions(token);
      if (seq === loadSeq.current) setSessions(rows);
    } catch (cause) {
      if (seq === loadSeq.current) {
        setLoadError(errorMessage(cause, "Não foi possível carregar as sessões."));
      }
    }
  }, [token]);

  useEffect(() => {
    if (oidcEnabled) void load();
  }, [load]);

  // As passkeys cadastradas (2026-10-21), só de quem entra com senha: pelo
  // Google não há o que listar. Mesmo descarte por carga das sessões.
  const [passkeys, setPasskeys] = useState<MyPasskey[] | null>(null);
  const [passkeyError, setPasskeyError] = useState<string | null>(null);
  const [removingPasskey, setRemovingPasskey] = useState(false);
  const passkeySeq = useRef(0);

  const loadPasskeys = useCallback(async () => {
    const seq = ++passkeySeq.current;
    setPasskeyError(null);
    try {
      const rows = await apiClient.listMyPasskeys(token);
      if (seq === passkeySeq.current) setPasskeys(rows);
    } catch (cause) {
      if (seq === passkeySeq.current) {
        setPasskeyError(errorMessage(cause, "Não foi possível carregar as chaves de acesso."));
      }
    }
  }, [token]);

  useEffect(() => {
    if (oidcEnabled && !signsInWithGoogle) void loadPasskeys();
  }, [loadPasskeys, signsInWithGoogle]);

  const removePasskey = async (passkey: MyPasskey) => {
    setRemovingPasskey(true);
    setPasskeyError(null);
    try {
      await apiClient.removeMyPasskey(token, passkey.id);
    } catch (cause) {
      // Sem recarregar: a lista não mudou, e a recarga apagaria a mensagem.
      setPasskeyError(errorMessage(cause, "Não foi possível remover a chave de acesso."));
      return;
    } finally {
      setRemovingPasskey(false);
    }
    await loadPasskeys();
  };

  const startAction = (action: AccountAction) => {
    setActionError(null);
    beginLogin({ action }).catch(cause =>
      setActionError(errorMessage(cause, "Não foi possível abrir a tela de segurança."))
    );
  };

  const end = async (targets: MySession[]) => {
    setBusy(true);
    setSessionError(null);
    try {
      for (const session of targets) {
        await apiClient.endMySession(token, session.id);
      }
      if (targets.some(session => session.current)) {
        logout();
        return;
      }
    } catch (cause) {
      setSessionError(errorMessage(cause, "Não foi possível encerrar a sessão."));
    } finally {
      setBusy(false);
    }
    // A lista depois do erro também: parte das sessões pode ter saído.
    await load();
  };

  if (!oidcEnabled) {
    return (
      <div className="ajustes-panel account-panel security-page">
        <header className="panel-header">
          <div>
            <h2>Senha e sessões</h2>
            <p>Como você entra no ReputAItion e onde está conectado.</p>
          </div>
        </header>
        {/* Mesma seção das outras páginas de Configurações (2026-10-16): o
            cartão solto ficava colado no cabeçalho, com outra formatação. */}
        <section className="panel-section">
          <h3>Segurança da conta</h3>
          <p className="account-section-hint">
            Este ambiente não tem um serviço de autenticação configurado,
            então não há senha nem sessão para gerenciar aqui.
          </p>
        </section>
      </div>
    );
  }

  // Esta sessão primeiro: é a que a pessoa procura, e a única com "Sair".
  const ordered = [...(sessions ?? [])].sort((a, b) => Number(b.current) - Number(a.current));
  const others = ordered.filter(session => !session.current);

  return (
    <div className="ajustes-panel account-panel security-page">
      <header className="panel-header">
        <div>
          <h2>Senha e sessões</h2>
          <p>Como você entra no ReputAItion e onde está conectado.</p>
        </div>
      </header>

      <section className="card account-card">
        <div className="card-title-header">
          <ShieldCheck size={20} className="card-icon" />
          <div>
            <h3>Como você entra</h3>
            <p>A forma de entrar no ReputAItion e o que dá para mudar aqui.</p>
          </div>
        </div>

        {/* Cada item é uma LINHA com a ação na mesma coluna, à direita.
            Antes as linhas reaproveitavam o cabeçalho de cartão e o botão
            ficava colado no texto, numa posição diferente em cada linha. */}
        <ul className="security-list">
          {signsInWithGoogle ? (
            <li className="security-row">
              <span className="security-tile is-accent" aria-hidden="true"><ShieldCheck size={18} /></span>
              <div className="security-row-copy">
                <strong>Conta Google</strong>
                <span>
                  Senha e verificação em duas etapas são as da sua conta Google,
                  em myaccount.google.com.
                </span>
              </div>
            </li>
          ) : (
            <>
              <li className="security-row">
                <span className="security-tile" aria-hidden="true"><KeyRound size={18} /></span>
                <div className="security-row-copy">
                  <strong>Senha</strong>
                  <span>Você entra com e-mail e senha. Para trocar, confirma a atual e escolhe a nova.</span>
                </div>
                <button type="button" className="secondary-button" onClick={() => startAction("UPDATE_PASSWORD")}>
                  <KeyRound size={15} />
                  Trocar senha
                </button>
              </li>
              <li className="security-row">
                <span className="security-tile" aria-hidden="true"><Smartphone size={18} /></span>
                <div className="security-row-copy">
                  <strong>Verificação em duas etapas</strong>
                  <span>O aplicativo autenticador do celular. Cadastre de novo ao trocar de aparelho.</span>
                </div>
                <button type="button" className="secondary-button" onClick={() => startAction("CONFIGURE_TOTP")}>
                  <Smartphone size={15} />
                  Configurar autenticador
                </button>
              </li>
              {/* A passkey troca senha + código por um toque no aparelho
                  (pedido dos usuários, 2026-10-21: o código a cada login
                  cansava). Quem não tem, ou está noutro aparelho, segue
                  com senha + código ("Tentar outra forma" na tela). */}
              <li className="security-row">
                <span className="security-tile" aria-hidden="true"><Fingerprint size={18} /></span>
                <div className="security-row-copy">
                  <strong>Chave de acesso (passkey)</strong>
                  <span>
                    Entre com a digital, o rosto ou o PIN do computador ou do celular,
                    sem senha e sem código. Cadastre em cada aparelho que você usa.
                    {passkeys?.length === 0 && " Nenhuma cadastrada ainda."}
                  </span>
                </div>
                <button
                  type="button"
                  className="secondary-button"
                  onClick={() => startAction("webauthn-register-passwordless")}
                >
                  <Fingerprint size={15} />
                  Cadastrar chave de acesso
                </button>
              </li>
              {/* Cada passkey cadastrada, com "Remover" pra a do aparelho
                  perdido ou trocado — sem o console do Keycloak. */}
              {passkeys?.map(passkey => {
                const name = passkey.label ?? "Chave de acesso";
                const created = passkey.created_at ? serverDate(passkey.created_at) : null;
                return (
                  <li className="security-row is-sub" key={passkey.id}>
                    <span className="security-tile" aria-hidden="true"><Fingerprint size={16} /></span>
                    <div className="security-row-copy">
                      <strong>{name}</strong>
                      <span>{created ? `Cadastrada em ${formatDateTime(created)}` : "Data de cadastro desconhecida"}</span>
                    </div>
                    <button
                      type="button"
                      className="secondary-button security-end-button"
                      disabled={removingPasskey}
                      aria-label={`Remover ${name}`}
                      onClick={() => void removePasskey(passkey)}
                    >
                      <Trash2 size={15} />
                      Remover
                    </button>
                  </li>
                );
              })}
            </>
          )}
        </ul>
        {actionError && <ApiState kind="error">{actionError}</ApiState>}
        {passkeyError && (
          <ApiState kind="error" onRetry={() => void loadPasskeys()}>
            {passkeyError}
          </ApiState>
        )}
      </section>

      <section className="card account-card">
        <div className="card-title-header">
          <Monitor size={20} className="card-icon" />
          <div>
            <h3>Sessões ativas</h3>
            <p>
              Onde você está com o ReputAItion aberto. Encerrar uma sessão
              desconecta aquele navegador também dos outros sistemas da FSB
              abertos nele.
            </p>
          </div>
        </div>

        {sessionError && <ApiState kind="error">{sessionError}</ApiState>}

        {loadError ? (
          <ApiState kind="error" onRetry={() => void load()}>
            {loadError}
          </ApiState>
        ) : sessions == null ? (
          <ApiState kind="loading">Carregando sessões…</ApiState>
        ) : sessions.length === 0 ? (
          <p className="security-empty">Nenhuma sessão ativa.</p>
        ) : (
          <>
            <ul className="security-list">
              {ordered.map(session => (
                <li className={`security-row${session.current ? " is-current" : ""}`} key={session.id}>
                  <span className={`security-tile${session.current ? " is-accent" : ""}`} aria-hidden="true">
                    <Monitor size={18} />
                  </span>
                  <div className="security-row-copy">
                    <strong>
                      {session.current ? "Esta sessão" : session.ip_address ?? "Endereço desconhecido"}
                      {session.current && <span className="security-current-chip">Você está aqui</span>}
                    </strong>
                    <span>
                      {session.current && session.ip_address && (
                        <><span className="security-ip">{session.ip_address}</span> · </>
                      )}
                      Desde {formatDateTime(serverDate(session.started_at))} · último acesso{" "}
                      {formatDateTime(serverDate(session.last_access_at))}
                    </span>
                  </div>
                  <button
                    type="button"
                    className={`secondary-button${session.current ? "" : " security-end-button"}`}
                    disabled={busy}
                    onClick={() => void end([session])}
                  >
                    <LogOut size={15} />
                    {session.current ? "Sair" : "Encerrar"}
                  </button>
                </li>
              ))}
            </ul>
            {others.length > 0 && (
              <div className="security-sessions-foot">
                <span>
                  {others.length === 1 ? "1 outra sessão aberta" : `${others.length} outras sessões abertas`}
                </span>
                <button
                  type="button"
                  className="secondary-button security-end-button"
                  disabled={busy}
                  onClick={() => void end(others)}
                >
                  <LogOut size={15} />
                  Encerrar todas as outras
                </button>
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}
